import { wsBaseUrl } from "@constants";
import React from "react";
import {
  CitationViewItem,
  formatDocLabel,
  formatTabularLabel,
  mergeCitationItems,
  normalizeNewCitationPacket,
} from "@pages/Assistant/utils/citations";
import { InlineBadge } from "@pages/Assistant/utils/messageParts";
import { LoadingEventType } from "@pages/Assistant/utils/useLoadingStream";

const useReportAssistantSocket = ({
  reportId,
  projectId,
  projectName,
  chatSessionId,
  setChatSessionId,
  conversationIdRef,
  setConversationId,
  chatLog,
  setChatLog,
  question,
  setQuestion,
  setIsFirstInteraction,
  setIsChatLoading,
  socket,
  setSocket,
  addMessageToChat,
  renderBotMessage,
  setShowLoading,
  clearLoading,
  setError,
  scrollToBottom,
  openReportEditingSocket,

  citationsV2Ref,
  inlineBadgesRef,
  nextBadgeNumRef,
  imageRef,
  thinkingReceived,
  ingest,
}: {
  reportId: string | undefined;
  projectId: string | undefined;
  projectName: string | undefined;
  chatSessionId: string | null;
  setChatSessionId: (id: string | null) => void;
  conversationIdRef: React.RefObject<string | null>;
  setConversationId: React.Dispatch<React.SetStateAction<string>>;
  chatLog: {
    message: string | React.ReactNode | React.ReactNode[];
    sender: "user" | "bot";
    citations?: any[];
    generated_images_s3_keys?: any;
  }[];
  setChatLog: React.Dispatch<
    React.SetStateAction<
      {
        message: string | React.ReactNode | React.ReactNode[];
        sender: "user" | "bot";
        citations?: any[];
        images?: any;
        generated_images_s3_keys?: any;
      }[]
    >
  >;
  question: string;
  setQuestion: React.Dispatch<React.SetStateAction<string>>;
  setIsFirstInteraction: React.Dispatch<React.SetStateAction<boolean>>;
  setIsChatLoading: React.Dispatch<React.SetStateAction<boolean>>;
  socket: WebSocket | null;
  setSocket: React.Dispatch<React.SetStateAction<WebSocket | null>>;
  addMessageToChat: (
    message: string | React.ReactNode | React.ReactNode[],
    sender: "user" | "bot",
    isChunk?: boolean,
    citations?: any[],
    generated_images_s3_keys?: any
  ) => void;
  renderBotMessage: (text: string) => void;
  setShowLoading: React.Dispatch<React.SetStateAction<boolean>>;
  clearLoading: () => void;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  scrollToBottom: () => void;
  openReportEditingSocket: (taskId: string) => void;
  citationsV2Ref: React.RefObject<CitationViewItem[]>;
  inlineBadgesRef: React.RefObject<InlineBadge[]>;
  nextBadgeNumRef: React.RefObject<number>;
  imageRef: React.RefObject<any[]>;
  thinkingReceived: React.RefObject<boolean>;
  ingest: (type: LoadingEventType, content: string) => void;
}) => {
  const connectWebSocket = (
    userQuestion: string,
    projName: string,
    projId: string
  ) => {
    setError(null);
    citationsV2Ref.current = [];
    inlineBadgesRef.current = [];
    nextBadgeNumRef.current = 1;
    thinkingReceived.current = false;

    if (!reportId || (!projName && !projId)) return;

    const body = {
      question: userQuestion,
      project_name: projName,
      project_id: projId,
      report_id: reportId,
      session_id: chatSessionId,
      conversation_id: conversationIdRef.current,
      is_report_editor: true,
    };

    const ws = new WebSocket(`${wsBaseUrl}/chat/report_chat`);
    ws.onopen = () => {
      ws.send(JSON.stringify(body));
    };

    let accumulatedMessage = "";

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);

      // ---- session init
      if (data.type === "session_init") {
        setChatSessionId(data.session_id);
        return;
      }
      // ---- conversation id
      if (data.type === "conversation_id") {
        setConversationId(data.content);
        conversationIdRef.current = data.content;
        return;
      }
      // ---- report editing task started
      if (data.type === "task_started") {
        const taskId = data.task_id;
        if (taskId && taskId !== "null") openReportEditingSocket(taskId);
        return;
      }

      // ---- loading events (separate panels; keep visible if thinking seen)
      if (
        data.type === "step" ||
        data.type === "thinking" ||
        data.type === "reflecting" ||
        data.type === "code_generation_start" ||
        data.type === "code_execution_start" ||
        data.type === "generated_plots_processing"
      ) {
        setIsChatLoading(true);
        setShowLoading(true);

        if (data.type === "thinking" && !thinkingReceived.current) {
          thinkingReceived.current = true;
        }

        ingest(data.type, data.content || "");
        return;
      }

      // ---- chunk: stream message; hide loader immediately only if no thinking
      if (data.type === "chunk") {
        const piece = data.content || "";
        accumulatedMessage += piece;
        renderBotMessage(accumulatedMessage);

        if (piece !== "" && !thinkingReceived.current) {
          setShowLoading(false);
        }
        return;
      }

      // ---- citation: build / merge + insert badge marker
      if (data.type === "citation") {
        const mergedItems = normalizeNewCitationPacket(data.content);
        citationsV2Ref.current = mergeCitationItems(
          citationsV2Ref.current,
          mergedItems
        );

        const hoverLines: string[] = [];
        for (const it of mergedItems) {
          if (it.kind === "web") {
            const host = (() => {
              try {
                return new URL(it.url).hostname;
              } catch {
                return it.url;
              }
            })();
            const title = it.title || host;
            hoverLines.push(`${title} — ${host}`);
          } else if (it.kind === "document") {
            hoverLines.push(formatDocLabel(it));
          } else {
            hoverLines.push(formatTabularLabel(it));
          }
        }
        const hoverText = hoverLines.map((line) => `• ${line}`).join("\n");

        let primary: Partial<InlineBadge> = {};
        const first = mergedItems[0];
        if (first?.kind === "document") {
          const pageAnchor =
            (first as any).overall?.[0] ??
            (first as any).page_ranges?.[0]?.[0] ??
            (first as any).pages?.[0];
          primary = {
            kind: "document",
            file_id: first.file_id,
            original_file: first.file_name,
            page_number: pageAnchor,
          };
        } else if (first?.kind === "tabular") {
          const [sheet, payload] =
            Object.entries((first as any).sheet_ranges || {})[0] || [];
          const rowAnchor = payload
            ? (payload as any).overall?.[0] ?? (payload as any).ranges?.[0]?.[0]
            : undefined;
          primary = {
            kind: "tabular",
            file_id: first.file_id,
            original_file: first.file_name,
            sheet_name: sheet,
            start_row: rowAnchor,
          };
        } else if (first?.kind === "web") {
          primary = {
            kind: "web",
            url: (first as any).url,
            title: (first as any).title,
          };
        }

        const n = nextBadgeNumRef.current++;
        inlineBadgesRef.current.push({
          n,
          project_id: projectId ? String(projectId) : null,
          hoverText,
          ...primary,
        });

        accumulatedMessage += ` {[CIT:${n}]}`;
        renderBotMessage(accumulatedMessage);
        return;
      }

      // ---- generated plots: append IMG tokens inline, keep images in ref
      if (data.type === "plots_data") {
        const newImages: any[] = Array.isArray(data.content)
          ? data.content
          : [];
        const baseIndex = imageRef.current.length;

        for (let i = 0; i < newImages.length; i++) {
          imageRef.current.push(newImages[i]);
          accumulatedMessage += ` {[IMG:${baseIndex + i}]}`;
        }

        renderBotMessage(accumulatedMessage);
        return;
      }

      // ---- error
      if (data.type === "error") {
        setIsChatLoading(false);
        setError("Something unexpected occurred. Please try again.");
        console.error(data.content);
        clearLoading();
        setShowLoading(false);
        thinkingReceived.current = false;
        ws.close();
        return;
      }

      // ---- end-of-turn
      if (data.type === "end") {
        if (accumulatedMessage) {
          addMessageToChat(
            accumulatedMessage,
            "bot",
            true,
            undefined,
            imageRef.current
          );

          // replace last bot message with final text (nodes will rehydrate on view)
          setChatLog((prev) => {
            const updated = [...prev];
            const lastBotIndex = updated
              .map((m) => m.sender)
              .lastIndexOf("bot");
            if (lastBotIndex !== -1) {
              updated[lastBotIndex] = {
                ...updated[lastBotIndex],
                message: accumulatedMessage,
              };
            }
            return updated;
          });
        }

        setIsChatLoading(false);
        clearLoading();
        setShowLoading(false);
        thinkingReceived.current = false;
        ws.close();
        return;
      }
    };

    ws.onerror = () => setError("Connection error occurred");

    ws.onclose = () => {
      if (accumulatedMessage) renderBotMessage(accumulatedMessage);
      setIsChatLoading(false);
      clearLoading();
      setShowLoading(false);
      thinkingReceived.current = false;
    };

    setSocket(ws);
  };

  const sendQuestion = (q: string) => {
    if (!q.trim()) return;

    // reset inline images for a fresh turn
    imageRef.current = [];

    setIsChatLoading(true);
    addMessageToChat(q, "user");
    if (socket) socket.close();

    // reset citation accumulators for the next turn
    citationsV2Ref.current = [];
    inlineBadgesRef.current = [];
    nextBadgeNumRef.current = 1;
    thinkingReceived.current = false;

    connectWebSocket(q, projectName || "", projectId || "");
    setQuestion("");
    setIsFirstInteraction(false);
    scrollToBottom();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendQuestion(question);
  };

  const tryAgain = () => {
    if (chatLog.length === 0) return;

    const lastUserIndex = [...chatLog].map((m) => m.sender).lastIndexOf("user");
    if (lastUserIndex === -1) return;

    const lastUserMessage = chatLog[lastUserIndex];
    const updatedHistory = chatLog.slice(0, lastUserIndex);

    // keep UI consistent
    setChatLog(updatedHistory);

    if (typeof lastUserMessage.message === "string") {
      sendQuestion(lastUserMessage.message);
    }
  };

  return { handleSubmit, tryAgain, sendQuestion };
};

export default useReportAssistantSocket;
